import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

export default function ChangePassword({ forced, open, onClose }) {
  const { setUser } = useAuth();
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (nw.length < 8) return setErr("New password must be at least 8 characters.");
    if (nw !== confirm) return setErr("New passwords do not match.");
    setLoading(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setUser((u) => (u ? { ...u, must_change_password: false } : u));
      setCur(""); setNw(""); setConfirm("");
      onClose?.();
    } catch (e2) {
      setErr(apiError(e2.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={forced ? () => {} : onClose}>
      <DialogContent className={`max-w-sm ${forced ? "[&>button]:hidden" : ""}`}>
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {forced && <ShieldAlert className="w-5 h-5 text-amber-500" />}
            {forced ? "Set a new password" : "Change password"}
          </DialogTitle>
          <DialogDescription>
            {forced
              ? "For security, you must replace your temporary password before continuing."
              : "Update your account password."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label>{forced ? "Temporary Password" : "Current Password"}</Label>
            <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} required data-testid="current-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label>New Password</Label>
            <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} required data-testid="new-password-input" />
          </div>
          <div className="space-y-1.5">
            <Label>Confirm New Password</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="confirm-password-input" />
          </div>
          {err && <p className="text-sm text-destructive" data-testid="change-password-error">{err}</p>}
          <Button type="submit" disabled={loading} data-testid="change-password-submit" className="w-full rounded-lg font-semibold active:scale-[0.98]">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update Password"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
